import { transformPoint, type Matrix, type Point, type Rect } from '../core/geometry';
import { canonicalizeRegions } from '../core/selection';
import type { PathNode, PathObject } from './types';
import { effectiveObjectPathGeometry } from './path';

interface Crossing {
  x: number;
  winding: number;
}

function midpoint(left: Point, right: Point): Point {
  return { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 };
}

function pointLineDistance(point: Point, start: Point, end: Point): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length < 0.0001) return Math.hypot(point.x - start.x, point.y - start.y);
  return Math.abs((dy * point.x) - (dx * point.y) + (end.x * start.y) - (end.y * start.x)) / length;
}

function flattenCubic(start: Point, control1: Point, control2: Point, end: Point, output: Point[], depth = 0): void {
  if (
    depth >= 12
    || Math.max(pointLineDistance(control1, start, end), pointLineDistance(control2, start, end)) <= 0.35
  ) {
    output.push(end);
    return;
  }
  const a = midpoint(start, control1);
  const b = midpoint(control1, control2);
  const c = midpoint(control2, end);
  const d = midpoint(a, b);
  const e = midpoint(b, c);
  const center = midpoint(d, e);
  flattenCubic(start, a, d, center, output, depth + 1);
  flattenCubic(center, e, c, end, output, depth + 1);
}

function flattenSubpath(nodes: readonly PathNode[], transform: Matrix): Point[] {
  const first = nodes[0];
  if (!first) return [];
  const points = [transformPoint(transform, first.anchor)];
  for (let index = 1; index < nodes.length; index += 1) {
    const previous = nodes[index - 1];
    const node = nodes[index];
    if (!previous || !node) continue;
    const start = transformPoint(transform, previous.anchor);
    const end = transformPoint(transform, node.anchor);
    if (previous.outgoing || node.incoming) {
      flattenCubic(
        start,
        transformPoint(transform, previous.outgoing ?? previous.anchor),
        transformPoint(transform, node.incoming ?? node.anchor),
        end,
        points,
      );
    } else points.push(end);
  }
  const last = nodes.at(-1);
  if (last && nodes.length > 1 && (last.outgoing || first.incoming)) {
    flattenCubic(
      transformPoint(transform, last.anchor),
      transformPoint(transform, last.outgoing ?? last.anchor),
      transformPoint(transform, first.incoming ?? first.anchor),
      transformPoint(transform, first.anchor),
      points,
    );
  }
  return points;
}

function crossingsAt(polygons: readonly Point[][], y: number): Crossing[] {
  const crossings: Crossing[] = [];
  for (const points of polygons) {
    if (points.length < 3) continue;
    for (let index = 0; index < points.length; index += 1) {
      const start = points[index];
      const end = points[(index + 1) % points.length];
      if (!start || !end || start.y === end.y) continue;
      const upward = start.y <= y && end.y > y;
      const downward = end.y <= y && start.y > y;
      if (!upward && !downward) continue;
      const amount = (y - start.y) / (end.y - start.y);
      crossings.push({ x: start.x + ((end.x - start.x) * amount), winding: upward ? 1 : -1 });
    }
  }
  return crossings.sort((left, right) => left.x - right.x);
}

function spanRect(left: number, right: number, y: number, bounds: Rect): Rect | null {
  const start = Math.max(Math.floor(bounds.x), Math.ceil(left - 0.5));
  const end = Math.min(Math.ceil(bounds.x + bounds.width), Math.ceil(right - 0.5));
  return end > start ? { x: start, y, width: end - start, height: 1 } : null;
}

export function pathToSelectionRegions(path: PathObject, transform: Matrix, bounds: Rect): Rect[] {
  const geometry = effectiveObjectPathGeometry(path);
  const nodesById = new Map(geometry.nodes.map((node) => [node.id, node]));
  const polygons = geometry.subpaths.map((subpath) => flattenSubpath(subpath.nodeIds.flatMap((id) => {
    const node = nodesById.get(id);
    return node ? [node] : [];
  }), transform)).filter((points) => points.length >= 3);
  return polygonSelectionRegions(polygons, path.geometry.fillRule === 'evenodd' ? 'evenodd' : 'nonzero', bounds);
}

/**
 * Scan-converts closed polygons (document space) into selection runs: a pixel
 * is selected when its centre is inside under `fillRule`. Rows are clipped to
 * `bounds`. Shared by Path to Selection and the `selection.polygon` command.
 */
export function polygonSelectionRegions(input: readonly (readonly Point[])[], fillRule: 'nonzero' | 'evenodd', bounds: Rect): Rect[] {
  const polygons = input.filter((points) => points.length >= 3).map((points) => [...points]);
  if (!polygons.length) return [];
  const points = polygons.flat();
  const top = Math.max(Math.floor(bounds.y), Math.floor(Math.min(...points.map((point) => point.y))));
  const bottom = Math.min(Math.ceil(bounds.y + bounds.height), Math.ceil(Math.max(...points.map((point) => point.y))));
  const regions: Rect[] = [];
  for (let y = top; y < bottom; y += 1) {
    const crossings = crossingsAt(polygons, y + 0.5);
    if (fillRule === 'evenodd') {
      for (let index = 0; index + 1 < crossings.length; index += 2) {
        const region = spanRect(crossings[index]!.x, crossings[index + 1]!.x, y, bounds);
        if (region) regions.push(region);
      }
      continue;
    }
    let winding = 0;
    let start: number | null = null;
    for (const crossing of crossings) {
      const previous = winding;
      winding += crossing.winding;
      if (previous === 0 && winding !== 0) start = crossing.x;
      if (previous !== 0 && winding === 0 && start !== null) {
        const region = spanRect(start, crossing.x, y, bounds);
        if (region) regions.push(region);
        start = null;
      }
    }
  }
  return canonicalizeRegions(regions);
}
